// ConfirmSmsDialog.tsx
import React from 'react';
import styled from 'styled-components';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Icon
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useAllowSmsNotificationsMutation } from 'services/api/notifications';
import { useDispatch, useSelector } from 'react-redux';
import { snackShow, UPDATE_PROFILE_SUCCESS, updateSmsAllowed } from 'actions';
import { useHistory } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import { closeAllowSmsDialog, openAllowSmsDialog } from 'services/userSlice/userSlice';

interface Props {
  onClose?: () => void;
}

const StyledDialog = styled(Dialog)`
  .allow-sms-paper {
    width: 367px;
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
`;

const Title = styled.div`
  font-weight: 500;
  font-size: 22px;
  color: #000;
`;

const Close = styled(IconButton)`
  position: absolute;
  top: 20px;
  right: 24px;
`;

const Copy = styled(Typography)`
  && {
    font-size: 1.125rem;
    line-height: 1.5;
    color: #6e6e6e;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
`;

const ChangeNumberButton = styled(Button)`
  && {
    flex: 1;
    background: #d9d9d9;
    color: #757575;
    font-weight: 500;
    height: 44px;
    font-size: 16px;
    border-radius: 6px;
    padding: 8px 0;
    &:hover {
      background: #d5d5d5;
    }
  }
`;

const ConfirmButton = styled(Button)`
  && {
    flex: 1;
    background: #225a91;
    color: #fff;
    font-weight: 500;
    height: 44px;
    border-radius: 6px;
    padding: 8px 0;
    &:hover {
      background: #0c4380;
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledContent = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  color: #757575;
`;

const AllowSmsDialog: React.FC<Props> = ({ onClose }) => {
  const [allowSms] = useAllowSmsNotificationsMutation();
  const dispatch = useDispatch();
  const history = useHistory();

  const open = useSelector((state: any) => state.authUser?.allowSmsDialogOpened);
  const close = () => {
    dispatch(closeAllowSmsDialog());
    if (onClose) {
      onClose();
    }
  };

  const user = useSelector((state: any) => state.user.user);

  const phoneNumber = (() => {
    if (user.role === 'admin') {
      return user.options?.phone_number;
    }

    if (user.role === 'seller') {
      return user.seller?.phone_number;
    }

    return user.buyer?.phone_number;
  })();

  const handleConfirm = async () => {
    await allowSms();
    dispatch(snackShow({ message: 'SMS Notifications allowed' }));

    const localStorageUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (Number(localStorageUser.id) !== Number(user.id)) {
      return;
    }
    localStorageUser.sms_notifications_allowed = true;
    localStorageUser.sms_notifications_enabled = true;

    localStorage.setItem('user', JSON.stringify(localStorageUser));

    dispatch({
      type: UPDATE_PROFILE_SUCCESS,
      payload: {
        data: {
          sms_notifications_allowed: true,
          sms_notifications_enabled: true
        }
      }
    });

    localStorage.setItem('user', JSON.stringify(localStorageUser));

    close();
  };

  const handleChangeNumber = () => {
    history.push('/profile/about_your_company');
    close();
  };

  return (
    <StyledDialog
      open={open}
      onClose={close}
      aria-labelledby="confirm-sms-title"
      classes={{ paperScrollPaper: 'allow-sms-paper' }}
    >
      <TitleRow>
        <Title>Confirm Receiving SMS</Title>
        <div style={{ display: 'flex' }} onClick={close}>
          <Icon>close</Icon>
        </div>
      </TitleRow>

      <StyledContent>
        By clicking “Confirm”, you agree to receive SMS messages, at the phone number provided&nbsp;
        <NumberFormat displayType="text" mask="_" format={'(###) ###-####.'} value={phoneNumber} />
      </StyledContent>

      <Actions>
        <ChangeNumberButton variant="contained" onClick={handleChangeNumber}>
          Change Number
        </ChangeNumberButton>

        <ConfirmButton variant="contained" onClick={handleConfirm}>
          Confirm
        </ConfirmButton>
      </Actions>
    </StyledDialog>
  );
};

export default AllowSmsDialog;
